import { AlertTriangle } from "lucide-react";
import { useTranslations } from "use-intl";
import { Badge } from "@/components/ui/badge";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { PaymentVerification } from "@/types/payments";
import { usePaymentVerificationDisplay } from "@/i18n/usePaymentVerificationDisplay";

interface PaymentVerificationBadgeProps {
  paymentVerification: PaymentVerification | null | undefined;
}

const BADGE_CLASSNAME: Record<PaymentVerification["status"], string> = {
  matched: "bg-green-50 text-green-700 border-green-200",
  unmatched_acknowledged: "bg-amber-50 text-amber-700 border-amber-200",
};

/**
 * Row-level indicator distinguishing an item paid via a real bank-transaction match from one
 * paid via the zero-match acknowledgment path (#498 Task 6 — design spec AC12: "Consultant,
 * scanning a list of paid items, can visually tell apart items paid via a real bank match from
 * items paid via unmatched-account acknowledgment, without opening each row"). Shared across
 * payables/receivables (InvoiceTableRow) and receipts (ReceiptsTable) — mirrors the existing
 * HoverCard-on-a-Badge pattern already used for InvoiceTableRow's TruncatedCell, rather than
 * inventing a new interaction.
 *
 * Renders nothing when there is no paymentVerification (e.g. the baseline "no bank accounts
 * connected" flow, which predates this feature) — the existing status Badge is left completely
 * unchanged for that case.
 */
export default function PaymentVerificationBadge({ paymentVerification }: PaymentVerificationBadgeProps) {
  const getDisplay = usePaymentVerificationDisplay();
  const display = getDisplay(paymentVerification);
  if (!display || !paymentVerification) return null;

  return (
    <>
      <HoverCard>
        <HoverCardTrigger asChild>
          <Badge
            variant="outline"
            className={`ml-2 cursor-help ${BADGE_CLASSNAME[paymentVerification.status]}`}
          >
            {display.label}
          </Badge>
        </HoverCardTrigger>
        <HoverCardContent className="w-72 text-sm">
          {display.description}
        </HoverCardContent>
      </HoverCard>
      <LaterMatchFlag paymentVerification={paymentVerification} />
    </>
  );
}

interface LaterMatchFlagProps {
  paymentVerification: PaymentVerification | null | undefined;
}

/**
 * "Later-match signal" (#498 Task 7 — design spec AC13/UX Requirement "Later-match signal"):
 * "Consultant, after acknowledging a payment with no match, and a matching transaction later
 * arrives through the normal sync process, sees a visible flag or indicator on that item
 * prompting review." Exported separately (not folded silently into the default export's JSX)
 * so it can be placed inside InvoiceDetailsDialog.tsx / ReceiptDetailDialog.tsx's own
 * hand-rolled verification section too — those two detail dialogs render their own inline
 * Badge instead of this component's default export (their description text must stay
 * always-visible, not hover-gated, per their existing Task 6 tests), so this is the shared
 * piece both the row-level badge and the detail dialogs reuse rather than each re-implementing
 * the same AlertTriangle-badge-plus-hover-explanation three times.
 *
 * Only ever relevant for `status === 'unmatched_acknowledged'` (a `matched` item has nothing
 * left to review) — gated on both the status AND the flag itself as defense in depth, since
 * the backend contract (PaymentVerificationService) already only ever sets
 * `laterMatchAvailable` for that status.
 *
 * Mirrors `DraftList.tsx`'s existing "Needs review" precedent: a `destructive`-variant Badge
 * with an `AlertTriangle` icon for a row-level flag prompting the consultant to look again,
 * rather than inventing a new visual pattern.
 */
export function LaterMatchFlag({ paymentVerification }: LaterMatchFlagProps) {
  const t = useTranslations("consultantPayables.verification");
  const show =
    !!paymentVerification
    && paymentVerification.status === "unmatched_acknowledged"
    && paymentVerification.laterMatchAvailable === true;
  if (!show) return null;

  return (
    <HoverCard>
      <HoverCardTrigger asChild>
        <Badge variant="destructive" className="ml-2 flex items-center gap-1 cursor-help w-fit">
          <AlertTriangle className="h-3 w-3" />
          {t("laterMatchTitle")}
        </Badge>
      </HoverCardTrigger>
      <HoverCardContent className="w-72 text-sm">
        {t("laterMatchBody")}
      </HoverCardContent>
    </HoverCard>
  );
}
